import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { mockLeads } from '@/mocks/leads';
import { mockOffers } from '@/mocks/offers';
import { mockPartners } from '@/mocks/partners';
import { Search, X, Terminal, Sparkles, ArrowRight, MessageSquare, BarChart3, Users, Inbox, FileText, Zap, ChevronRight } from 'lucide-react';

interface Suggestion {
  icon: React.ReactNode;
  label: string;
  description: string;
  action: () => void;
  shortcut?: string;
}

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [jsonVisible, setJsonVisible] = useState(false);
  const [jsonData, setJsonData] = useState<any>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
        setJsonVisible(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
      setActiveIndex(0);
    }
  }, [open]);

  const suggestions: Suggestion[] = [
    {
      icon: <Inbox size={14} strokeWidth={1.5} />,
      label: 'View unmatched leads',
      description: '12 leads need routing attention',
      action: () => { navigate('/leads'); setOpen(false); },
    },
    {
      icon: <BarChart3 size={14} strokeWidth={1.5} />,
      label: 'Show monthly report',
      description: 'Revenue and routing metrics',
      action: () => { navigate('/reports'); setOpen(false); },
    },
    {
      icon: <Users size={14} strokeWidth={1.5} />,
      label: 'Partner health check',
      description: '3 partners flagged for review',
      action: () => { navigate('/partners'); setOpen(false); },
    },
    {
      icon: <FileText size={14} strokeWidth={1.5} />,
      label: 'Pending invoices',
      description: '4 invoices awaiting approval',
      action: () => { navigate('/finance/invoices'); setOpen(false); },
    },
    {
      icon: <Zap size={14} strokeWidth={1.5} />,
      label: 'System audit',
      description: 'Deep-dive entity state analysis',
      action: () => {
        const leadMatch = location.pathname.match(/\/leads\/([^/]+)/);
        const offerMatch = location.pathname.match(/\/offers\/([^/]+)/);
        const partnerMatch = location.pathname.match(/\/partners\/([^/]+)/);
        let data: any = null;
        if (leadMatch) data = mockLeads.find(l => l.id === leadMatch![1]) || null;
        else if (offerMatch) data = mockOffers.find(o => o.id === offerMatch![1]) || null;
        else if (partnerMatch) data = mockPartners.find(p => p.id === partnerMatch![1]) || null;
        setJsonData(data);
        setJsonVisible(true);
        setOpen(false);
        setQuery('');
      },
      shortcut: '⌘+Enter',
    },
  ];

  const filteredSuggestions = suggestions.filter(s =>
    query.length === 0 ||
    s.label.toLowerCase().includes(query.toLowerCase()) ||
    s.description.toLowerCase().includes(query.toLowerCase())
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (filteredSuggestions[activeIndex]) {
      filteredSuggestions[activeIndex].action();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => Math.min(prev + 1, filteredSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredSuggestions[activeIndex]) {
        filteredSuggestions[activeIndex].action();
      }
    }
  };

  return (
    <>
      {/* Command palette overlay */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[16vh] animate-fade-in">
          <div
            className="absolute inset-0"
            style={{ background: 'var(--overlay-backdrop)', backdropFilter: 'blur(8px)' }}
            onClick={() => setOpen(false)}
          />
          <div className="relative w-full max-w-xl mx-4 rounded-2xl overflow-hidden border"
            style={{
              background: 'var(--bg-surface-raised)',
              backdropFilter: 'blur(40px)',
              WebkitBackdropFilter: 'blur(40px)',
              borderColor: 'var(--border-primary)',
              boxShadow: 'var(--shadow-card-hover)',
            }}
          >
            {/* Nova header */}
            <div className="flex items-center gap-2 px-5 py-3 border-b"
              style={{ borderColor: 'var(--border-separator)' }}
            >
              <div className="w-6 h-6 rounded-full flex items-center justify-center"
                style={{ background: 'var(--accent-dim)' }}
              >
                <Sparkles size={12} style={{ color: 'var(--accent)' }} />
              </div>
              <span className="text-[13px] font-semibold" style={{ color: 'var(--text-secondary)' }}>Nova</span>
              <span className="text-[11px] ml-auto" style={{ color: 'var(--text-muted)' }}>Your AI operating assistant</span>
            </div>

            <form onSubmit={handleSubmit} className="flex items-center px-5 py-4">
              <Search size={16} className="mr-3" style={{ color: 'var(--text-muted)' }} />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={e => { setQuery(e.target.value); setActiveIndex(0); }}
                onKeyDown={handleKeyDown}
                placeholder="Stel Nova een vraag..."
                className="flex-1 text-sm bg-transparent outline-none"
                style={{ color: 'var(--text-primary)' }}
              />
              {query && (
                <button type="button" onClick={() => setQuery('')} className="p-1 rounded-md transition-colors hover-surface"
                  style={{ color: 'var(--text-muted)' }}
                >
                  <X size={14} />
                </button>
              )}
              <span className="hidden sm:inline text-[11px] ml-3 border rounded-md px-1.5 py-0.5"
                style={{ color: 'var(--text-muted)', borderColor: 'var(--border-primary)' }}>
                ⌘K
              </span>
            </form>

            {/* Suggestion list */}
            <div className="px-3 pb-3 space-y-0.5">
              {filteredSuggestions.map((s, i) => (
                <button
                  key={s.label}
                  onClick={s.action}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-200"
                  style={{
                    background: i === activeIndex ? 'var(--bg-surface-hover)' : 'transparent',
                    border: i === activeIndex ? '1px solid var(--border-hover)' : '1px solid transparent',
                  }}
                  onMouseEnter={() => setActiveIndex(i)}
                >
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)' }}
                  >
                    {s.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium truncate" style={{ color: 'var(--text-primary)' }}>{s.label}</p>
                    <p className="text-[11px] truncate" style={{ color: 'var(--text-muted)' }}>{s.description}</p>
                  </div>
                  {s.shortcut ? (
                    <span className="text-[10px] border rounded-md px-1.5 py-0.5 shrink-0"
                      style={{ color: 'var(--text-muted)', borderColor: 'var(--border-primary)' }}>
                      {s.shortcut}
                    </span>
                  ) : (
                    <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />
                  )}
                </button>
              ))}
              {filteredSuggestions.length === 0 && (
                <div className="px-3 py-6 text-center">
                  <MessageSquare size={20} className="mx-auto mb-2" style={{ color: 'var(--text-muted)' }} />
                  <p className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>No suggestions match your query</p>
                  <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>Try typing a different keyword</p>
                </div>
              )}
            </div>

            <div className="px-5 py-2.5 border-t text-[11px] flex items-center gap-3"
              style={{ borderColor: 'var(--border-separator)', color: 'var(--text-muted)' }}
            >
              <span className="flex items-center gap-1"><ArrowRight size={10} /> to select</span>
              <span className="flex items-center gap-1">↑↓ to navigate</span>
              <span className="flex items-center gap-1">esc to close</span>
            </div>
          </div>
        </div>
      )}

      {/* JSON Viewer Modal */}
      {jsonVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
          <div
            className="absolute inset-0"
            style={{ background: 'var(--overlay-backdrop)', backdropFilter: 'blur(8px)' }}
            onClick={() => setJsonVisible(false)}
          />
          <div className="relative w-full max-w-3xl max-h-[80vh] rounded-2xl flex flex-col border overflow-hidden"
            style={{
              background: 'var(--bg-surface-raised)',
              backdropFilter: 'blur(40px)',
              WebkitBackdropFilter: 'blur(40px)',
              borderColor: 'var(--border-primary)',
              boxShadow: 'var(--shadow-card-hover)',
            }}
          >
            <div className="flex items-center justify-between px-6 py-3 border-b"
              style={{ borderColor: 'var(--border-separator)' }}
            >
              <div className="flex items-center gap-2">
                <Terminal size={16} style={{ color: 'var(--text-tertiary)' }} />
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>System State Audit</h3>
              </div>
              <button onClick={() => setJsonVisible(false)} className="p-1.5 rounded-md transition-colors hover-surface"
                style={{ color: 'var(--text-tertiary)' }}
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              {jsonData ? (
                <pre className="text-xs font-mono p-4 rounded-xl overflow-auto border"
                  style={{ color: 'var(--text-secondary)', background: 'var(--bg-input)', borderColor: 'var(--border-primary)' }}
                >
                  {JSON.stringify(jsonData, null, 2)}
                </pre>
              ) : (
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No entity context available on this page.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}